import * as Yup from 'yup'
import text, {realTimeValidationText} from './validationText'
import monitoringTypes from '../data/monitoringTypes.json'
import protocols from '../data/protocols.json'
import authMethods from '../data/authMethods.json'
import regexPatterns from './regexPatterns'
import {DateFormats, isBeforeOrEqual, isTimeBefore} from './date'
import i18n from '../i18n'

const getFields = (key) => {
    return i18n.t(key, {ns: 'validation', returnObjects: true})
}

const statuspageFaviconMaxDimension = 192

const allowedProtocols = [
    'http',
    'https'
]
const MIN_LENGTH = 2
const MIN_DESC_LENGTH = 3
const PASSWORD_MIN_LENGTH = 8
const CODE_LENGTH = 6
const MIN_FEEDBACK_LENGTH = 20
const STATUS_CODE_MIN_VALUE = 100
const STATUS_CODE_MAX_VALUE = 599
const MIN_PORT_VALUE = 1
const MAX_PORT_VALUE = 65535
const STATUSPAGE_LOGO_MAX_SIZE = {
    value: 1 * 1024 * 1024,
    label: '1MB'
}
const STATUSPAGE_FAVICON_MAX_SIZE = {
    value: 256 * 1024,
    label: '256KB'
}

const PASSWORD_CHARS = '^(?=.*[!@#$%^&*:;=+|~`}{)(></"_\\[\\],\'.?-])'
const URL_CHARS = '^(?=.*[.])'
const PROTOCOL_CHARS = '^http'
const PORT_VALUES = {
    smtp: [25, 465, 587],
    imap: [143, 993],
    pop: [110, 995]
}

const typesArr = [monitoringTypes.port.value, monitoringTypes.email_protocols.value]

Yup.addMethod(Yup.string, 'host', function(message) {
    return this.test({
        name: 'host',
        message,
        test: value => {
            let url
            try {
                url = new URL(value)
            } catch (e) {
                url = new URL(`https://${value}`)
            }
            return !(url.search || url.port || url.pathname !== '/')
        }
    })
})

const addResourceSchema = (conditionTypes) => Yup.object().shape({
    name: Yup.string()
        .required(text.required(getFields('RESOURCE_NAME')))
        .min(MIN_LENGTH, text.length(getFields('RESOURCE_NAME'), MIN_LENGTH, 'minimum')),
    conditionOption: Yup.object(),
    keyword: Yup.string().nullable().when(['conditionOption', 'type'], {
        is: (conditionOption, type) => type !== monitoringTypes.icmp.value && [conditionTypes.does_contain.value, conditionTypes.does_contain.value].includes(conditionOption?.value),
        then: Yup.string().required(text.required(getFields('KEYWORD'))).trim().min(MIN_LENGTH, text.length(getFields('KEYWORD'), MIN_LENGTH, 'minimum')).test('len', '', (keywords = '') => {
            const keywordArray = [keywords]
            return keywordArray
        })
    }),
    url: Yup.string()
        .when('type', (data) => {
            if (data !== monitoringTypes.http.value) {
                return Yup.string()
                    .transform(value => value.trim())
                    .required(text.required(getFields('HOST')))
                    .matches(URL_CHARS, text.invalid_field(getFields('HOST')))
                    .host(text.invalid_field(getFields('HOST')))
            }
            return Yup.string()
                .transform(value => value.trim())
                .required(text.required(getFields('URL')))
                .url(text.invalid_field(getFields('URL')))
                .matches(PROTOCOL_CHARS, text.has_protocol(allowedProtocols))
                .matches(URL_CHARS, text.invalid_field(getFields('URL')))
        }),
    service_id: Yup.object().nullable()
        .required(text.required(getFields('SERVICE_NAME'))),
    team_id: Yup.object().nullable()
        .required(text.required(getFields('TEAM_NAME'))),
    regions: Yup.array().min(1, text.required(getFields('REGIONS'), true)),
    headers: Yup.array().nullable().when('is_advanced', (data) => {
        if (data) {
            return Yup.array().nullable().of(Yup.object().shape({
                key: Yup.string().when(['value'], {
                    is: value => value,
                    then: Yup.string().required(text.required(getFields('KEY')))
                }),
                value: Yup.string().when(['key'], {
                    is: key => key,
                    then: Yup.string().required(text.required(getFields('VALUE')))
                })
            }, [['key', 'value']]))
        }
    }),
    bearer_token: Yup.string().nullable().when('auth_method', data => {
        if (data?.value === authMethods.bearer_token.value) {
            return Yup.string().required(text.required(getFields('BEARER_TOKEN')))
        }
    }),
    username: Yup.string().nullable().when('auth_method', data => {
        if (data?.value === authMethods.basic_auth.value) {
            return Yup.string().required(text.required(getFields('USERNAME')))
        }
    }),
    password: Yup.string().nullable().when('auth_method', data => {
        if (data?.value === authMethods.basic_auth.value) {
            return Yup.string().required(text.required(getFields('PASSWORD')))
        }
    }),
    type: Yup.string(),
    conditions: Yup.object().nullable().when(['conditionOption', 'type'], (conditionOption, type) => {
        if (!conditionOption || type !== 'http') {
            return Yup.object().shape({})
        }

        return Yup.object().shape({
            [conditionOption.type]: Yup.array().test('check-length', function(data) {
                if (data?.every(({key, value}) => !key && !value) && conditionOption.value === conditionTypes.json_key_value_check.value) {
                    const condition = data[0]
                    const errors = []
                    if (!condition.key) {
                        errors.push(this.createError({path: `${this.path}.[0].key`, message:text.required(getFields('KEY'))}))
                    }
                    if (!condition.value) {
                        errors.push(this.createError({path: `${this.path}.[0].value`, message:text.required(getFields('VALUE'))}))
                    }
                    if (!condition.operator) {
                        errors.push(this.createError({path: `${this.path}.[0].operator`, message:text.required(getFields('OPERATOR'))}))
                    }
                    if (errors.length > 0) {
                        throw new Yup.ValidationError(errors)
                    }
                }
                return true
            }).of(
                Yup.object().shape({
                    key: Yup.string().nullable().when(['value', 'operator'], {
                        is: (value, operator) =>
                            conditionOption.value === conditionTypes.json_key_value_check.value && (operator ? operator && value : value),
                        then: Yup.string().required(text.required(getFields('KEY'))),
                        otherwise: Yup.string().nullable()
                    }),
                    operator: Yup.object().nullable().when(['value', 'key'], {
                        is: (value, key) => conditionOption.value === conditionTypes.json_key_value_check.value ? value || key : Yup.object().nullable().required(text.required(getFields('OPERATOR'))),
                        then: Yup.object().nullable().required(text.required(getFields('OPERATOR'))),
                        otherwise: Yup.object().nullable()
                    }),
                    value: Yup.mixed().when(['key', 'operator'], {
                        is: (key) => conditionOption.value === conditionTypes.json_key_value_check.value ? key : Yup.string().required(text.required(getFields('VALUE'))),
                        then: Yup.mixed().when([], {
                            is: () => conditionOption.value === conditionTypes.status_code_check.value,
                            then: Yup.number()
                                .typeError(text.must_be_type(getFields('STATUS_CODE'), getFields('NUMBER')))
                                .required(text.required(getFields('STATUS_CODE')))
                                .test(
                                    'is-between-100-599',
                                    text.must_be_between(getFields('STATUS_CODE'), STATUS_CODE_MIN_VALUE, STATUS_CODE_MAX_VALUE),
                                    value => value >= STATUS_CODE_MIN_VALUE && value <= 599
                                ),
                            otherwise: Yup.string().required(text.required(getFields('VALUE')))
                        }),
                        otherwise: Yup.mixed()
                    })
                },  [
                    ['key', 'value'], ['key', 'operator'],
                    ['operator', 'value']
                ])
            )
        })
    }),
    monitoring_protocol: Yup.object().nullable().when('type', data => {
        if (typesArr.includes(data)) {
            return Yup.object().nullable().required(text.required(getFields('PROTOCOL')))
        }
    }),
    port: Yup.number().nullable().when(['type', 'monitoring_protocol'], (type, additionalType) => {
        if ([monitoringTypes.port.value, monitoringTypes.udp.value, monitoringTypes.tcp.value].includes(type)) {
            return Yup.number().nullable().required(text.required(getFields('PORT')))
                .min(MIN_PORT_VALUE, text.must_be_between(getFields('PORT'), MIN_PORT_VALUE, MAX_PORT_VALUE))
                .max(MAX_PORT_VALUE, text.must_be_between(getFields('PORT'), MAX_PORT_VALUE, MAX_PORT_VALUE))
        }
        if (additionalType?.value && Object.values(protocols.smtp_imap_pop3.data[additionalType?.value])) {
            return Yup.number().nullable()
                .required(text.required(getFields('PORT')))
                .oneOf(PORT_VALUES[additionalType.value], text.one_of(getFields('PORT'), PORT_VALUES[additionalType.value]))
        }
        return Yup.number().nullable()
    }),
    maintenance_cron_expression: Yup.string().transform(value => value.trim())
        .matches(/^(@(annually|yearly|monthly|weekly|daily|hourly|reboot))|(@every (\d+(ns|us|µs|ms|s|m|h))+)|((((\d+,)+\d+|(\d+(\/|-)\d+)|\d+|\*) ?){5,7})$/, text.invalid_field(getFields('CRON_EXPRESSION'))),
    maintenance_duration: Yup.string()
})

const addDynamicRulesetSchema = (values) => {
    return Yup.object().shape(
        Object.fromEntries(
            Object.entries(values).map(([key, value]) => {
                if (value.pairs) {
                    return [key, buildPairsSchema()]
                }

                return [key, buildFieldSchema(key, value)]
            })
        )
    )
}

const getPairIndexFromPath = (path) => {
    const match = /\[(\d+)\]/.exec(path || '')
    return match ? Number(match[1]) : 0
}

const buildStrictPairSchema = () => Yup.object().shape({
    operator: Yup.object().nullable().when('values', {
        is: (values) => hasFieldValue(values),
        then: (schema) => Yup.object().shape({
            value: schema.required(text.required(getFields('OPERATOR')))
        }),
        otherwise: (schema) => schema
    }),
    values: Yup.object()
        .nullable()
        .when('operator', {
            is: (operator) => operator?.value?.value,
            then: () => Yup.object().shape({
                value: Yup.mixed()
                    .test(
                        'validate-condition-value-based-on-type',
                        text.required(getFields('VALUE')),
                        function(val) {
                            if (this.parent?.type === 'number') {
                                return !isNaN(Number(val)) && val !== '' && val !== null
                            }
                            return validateByFormType(val, this.parent?.form_type)
                        }
                    )
                    .test(
                        'validate-condition-value-range',
                        text.must_be_between(getFields('VALUE'), 0, 100),
                        function(val) {
                            const {min, max, type} = this.parent
                            if (type !== 'number' || min === undefined || max === undefined) return true
                            const numericValue = Number(val)
                            return isNaN(numericValue) || (numericValue >= min && numericValue <= max)
                        }
                    )
            }).required(text.required(getFields('VALUE'))),
            otherwise: Yup.object().nullable()
        }),
    last_time: Yup.object().nullable().shape({
        value: Yup.mixed().test(
            'validate-last-time',
            text.required(getFields('LAST_TIME')),
            (val) => val !== null && val !== undefined && val !== '' && !isNaN(Number(val)) && Number(val) >= 1
        )
    })
}, [
    ['operator', 'values']
])

const lenientPairSchema = Yup.object().shape({
    operator: Yup.object().nullable(),
    values: Yup.object().nullable(),
    last_time: Yup.object().nullable()
})

const buildPairsSchema = () => {
    return Yup.object().shape({
        pairs: Yup.array().of(
            Yup.lazy((pair, options) => {
                const index = getPairIndexFromPath(options?.path)
                const isValueEmpty = !hasFieldValue(pair?.values)

                return index > 0 && isValueEmpty ? lenientPairSchema : buildStrictPairSchema()
            })
        )
    })
}

const buildFieldSchema = (key, value) => {
    const fieldValue = value.value

    if (key === 'message') {
        return Yup.object().shape({
            value: Yup.string().nullable()
        })
    }

    if (typeof fieldValue === 'string') {
        return Yup.object().shape({
            value: Yup.string().required(text.required(getFields(key.toUpperCase())))
        })
    }

    if (typeof fieldValue === 'number') {
        return Yup.object().shape({
            value: Yup.number()
                .nullable()
                .required(text.required(getFields(key.toUpperCase())))
        })
    }

    if (typeof fieldValue === 'boolean') {
        return Yup.object().shape({
            value: Yup.boolean().required(text.required(getFields(key.toUpperCase())))
        })
    }

    if (Array.isArray(fieldValue)) {
        return Yup.object().shape({
            value: Yup.array()
                .min(1, text.required(getFields(key.toUpperCase())))
                .required(text.required(getFields(key.toUpperCase())))
        })
    }

    if (typeof fieldValue === 'object' && fieldValue !== null) {
        return Yup.object().shape({
            value: Yup.object()
                .nullable()
                .required(text.required(getFields(key.toUpperCase())))
        })
    }

    return Yup.object().shape({
        value: Yup.mixed().nullable()
    })
}

const hasFieldValue = (field) => {
    const val = field?.value
    if (val === null || val === undefined || val === '') return false
    if (typeof val === 'object' && 'value' in val) return val.value !== '' && val.value !== null
    return true
}

const validateByFormType = (val, formType) => {
    if (formType === 'input') {
        return typeof val === 'string' && val.trim() !== ''
    }
    if (formType === 'select') {
        return val !== null && typeof val === 'object' && 'value' in val
    }
    return !!val
}

const logManagementFiltersSchema = () => {
    return Yup.object({
        fieldPairs: Yup.array()
            .of(
                Yup.object({
                    field_name: Yup.string().nullable(),
                    condition: Yup.object().nullable(),
                    value: Yup.string().nullable()
                })
            )
            .test('validate-first-if-empty', '', function(fieldPairs) {
                if (!Array.isArray(fieldPairs) || fieldPairs.length === 0) return true

                const allEmpty = fieldPairs.every(
                    (pair) => !pair?.field_name?.trim() || !pair?.value?.trim()
                )

                if (!allEmpty) return true

                const first = fieldPairs[0] || {}
                const createErrorAt = (field, message) =>
                    this.createError({path: `${this.path}[0].${field}`, message})

                const errors = [
                    !first.field_name?.trim() && createErrorAt('field_name', text.required(getFields('NAME'))),
                    !first.condition?.value && createErrorAt('condition', text.required(getFields('CONDITION'))),
                    !first.value?.trim() && createErrorAt('value', text.required(getFields('VALUE')))
                ].filter(Boolean)

                return errors.length ? new Yup.ValidationError(errors) : true
            })
    })
}

const customerSchema = () => {
    return Yup.object().shape({
        name: Yup.string()
            .required(text.required(getFields('NAME')))
            .min(MIN_LENGTH, text.length(getFields('NAME'), MIN_LENGTH, 'minimum')),
        surname: Yup.string()
            .required(text.required(getFields('SURNAME')))
            .min(MIN_LENGTH, text.length(getFields('SURNAME'), MIN_LENGTH, 'minimum'))
    })
}

const scheduleSchema = () => {
    return Yup.object().shape({
        team_member: Yup.object().nullable().required(text.required(getFields('TEAM_MEMBER'))),
        start_time: Yup.string().required(text.choose_field(getFields('START_TIME'))),
        end_time: Yup.string().required(text.choose_field(getFields('START_TIME'))).test(
            'is-later-than-start',
            text.end_time_validation('End time', getFields('START_TIME')),
            function(value) {
                return isTimeBefore(this.parent.start_time, value)
            }
        ),
        end_date: Yup.string().test(
            'is-later-than-start',
            text.end_time_validation('End date', getFields('START_DATE')),
            function(value) {
                return isBeforeOrEqual(this.parent.start_date, value, DateFormats.DOT_DMY)
            }
        ),
        weekdays: Yup.array().min(1, text.choose_field(getFields('WEEKDAYS')))
    })
}

const usernameSchema = () => {
    return Yup.object().shape({
        email: Yup.string()
            .required(text.required(getFields('EMAIL')))
            .email(text.like_email(getFields('EMAIL')))
    })
}

const codeSchema = () => {
    return Yup.object().shape({
        code: Yup.string()
            .required(text.required(getFields('CODE')))
            .matches(`^[0-9]{${CODE_LENGTH}}$`, text.digits(getFields('CODE'), CODE_LENGTH))
    })
}

const mfaCodeSchema = () => {
    return Yup.object().shape({
        otp_code: Yup.string()
            .required(text.required(getFields('CODE')))
            .matches(`^[0-9]{${CODE_LENGTH}}$`, text.digits(getFields('CODE'), CODE_LENGTH))
    })
}

const teamSchema = () => {
    return Yup.object().shape({
        name: Yup.string()
            .required(text.required(getFields('NAME')))
            .min(MIN_LENGTH, text.length(getFields('TEAM_NAME'), MIN_LENGTH, 'minimum'))
    })
}

const feedbackSchema = () => {
    return Yup.object().shape({
        feedback: Yup.string()
            .required(text.required(getFields('FEEDBACK')))
            .min(MIN_FEEDBACK_LENGTH, text.length(getFields('FEEDBACK'), MIN_FEEDBACK_LENGTH, 'minimum'))
    })
}

const notificationEmailSchema = () => {
    return Yup.object().shape({
        value: Yup.string()
            .required(text.required(getFields('CHANNEL_EMAIL')))
            .email(text.like_email(getFields('CHANNEL_EMAIL')))
    })
}

const voucherSchema = () => {
    return Yup.object().shape({
        code: Yup.string().nullable()
            .required(text.required(getFields('VOUCHER')))
    })
}

const couponSchema = () => {
    return Yup.object().shape({
        code: Yup.string().nullable()
            .required(text.required(getFields('COUPON')))
    })
}

const channelTypeSchema = () => {
    return Yup.object().shape({
        type: Yup.object().nullable()
            .required(text.required(getFields('CHANNEL_TYPE')))
    })
}

const webhookSchema = () => {
    return Yup.object().shape({
        type: Yup.object().nullable()
            .required(text.required(getFields('CHANNEL_TYPE'))),
        value: Yup.string()
            .transform(value => value.trim())
            .required(text.required(getFields('URL')))
            .url(text.invalid_field(getFields('URL')))
            .matches(URL_CHARS, text.invalid_field(getFields('URL')))
            .matches(PROTOCOL_CHARS, text.has_protocol(allowedProtocols))
    })
}

const telegramSchema = () => {
    return Yup.object().shape({
        type: Yup.object().nullable()
            .required(text.required(getFields('CHANNEL_TYPE'))),
        value: Yup.string()
            .transform(value => value.trim())
            .required(text.required(getFields('CHAT_ID')))
    })
}

const webhookURLSchema = () => {
    return Yup.object().shape({
        value: Yup.string()
            .transform(value => value.trim())
            .required(text.required(getFields('WEBHOOK')))
            .url(text.invalid_field(getFields('WEBHOOK')))
            .matches(URL_CHARS, text.invalid_field(getFields('WEBHOOK')))
            .matches(PROTOCOL_CHARS, text.has_protocol(allowedProtocols))
    })
}

const automationSchema = () => {
    return Yup.object().shape({
        name: Yup.string()
            .required(text.required(getFields('NAME')))
            .min(MIN_DESC_LENGTH, text.length(getFields('NAME'), MIN_DESC_LENGTH, 'minimum')),
        description: Yup.string()
            .required(text.required(getFields('DESCRIPTION')))
            .min(MIN_DESC_LENGTH, text.length(getFields('DESCRIPTION'), MIN_DESC_LENGTH, 'minimum')),
        events: Yup.array().min(1, text.should_select(getFields('EVENT')))
    })
}

const reminderSchema = () => {
    return Yup.object().shape({
        days_before: Yup.array().min(1, text.should_select(getFields('REMINDER')))
    })
}

const deletionReasonSchema = () => {
    return Yup.object().shape({
        reason: Yup.object().nullable()
            .required(text.required(getFields('REASON')))
    })
}

const validationElement = (message, validator) => {
    return {message: message, validator: (value, oldValue) => validator(value, oldValue)}
}

const getPasswordErrors = async() => {
    return {
        previous_password: [
            validationElement(
                await realTimeValidationText.length(PASSWORD_MIN_LENGTH, getFields('MINIMUM')),
                value => Yup.string().min(PASSWORD_MIN_LENGTH).isValidSync(value)
            ),
            validationElement(
                await realTimeValidationText.has_case(getFields('LOWERCASE')),
                value => Yup.string().matches(/[a-z]/).isValidSync(value)
            ),
            validationElement(
                await realTimeValidationText.has_case(getFields('UPPERCASE')),
                value => Yup.string().matches(/[A-Z]/).isValidSync(value)
            ),
            validationElement(
                await realTimeValidationText.has_number(),
                value => Yup.string().matches(/[0-9]/).isValidSync(value)
            ),
            validationElement(
                await realTimeValidationText.has_char(),
                value => Yup.string().matches(new RegExp(PASSWORD_CHARS)).isValidSync(value)
            )
        ],

        proposed_password: [
            validationElement(
                await realTimeValidationText.length(PASSWORD_MIN_LENGTH, getFields('MINIMUM')),
                value => Yup.string().min(PASSWORD_MIN_LENGTH).isValidSync(value)
            ),
            validationElement(
                await realTimeValidationText.has_case(getFields('LOWERCASE')),
                value => Yup.string().matches(/[a-z]/).isValidSync(value)
            ),
            validationElement(
                await realTimeValidationText.has_case(getFields('UPPERCASE')),
                value => Yup.string().matches(/[A-Z]/).isValidSync(value)
            ),
            validationElement(
                await realTimeValidationText.has_number(),
                value => Yup.string().matches(/[0-9]/).isValidSync(value)
            ),
            validationElement(
                await realTimeValidationText.has_char(),
                value => Yup.string().matches(new RegExp(PASSWORD_CHARS)).isValidSync(value)
            )
        ],

        confirm_new_password: [
            validationElement(
                await realTimeValidationText.length(PASSWORD_MIN_LENGTH, getFields('MINIMUM')),
                value => Yup.string().min(PASSWORD_MIN_LENGTH).isValidSync(value)
            ),
            validationElement(
                await realTimeValidationText.has_case(getFields('LOWERCASE')),
                value => Yup.string().matches(/[a-z]/).isValidSync(value)
            ),
            validationElement(
                await realTimeValidationText.has_case(getFields('UPPERCASE')),
                value => Yup.string().matches(/[A-Z]/).isValidSync(value)
            ),
            validationElement(
                await realTimeValidationText.has_number(),
                value => Yup.string().matches(/[0-9]/).isValidSync(value)
            ),
            validationElement(
                await realTimeValidationText.has_char(),
                value => Yup.string().matches(new RegExp(PASSWORD_CHARS)).isValidSync(value)
            ),
            validationElement(
                await realTimeValidationText.is_same(getFields('PROPOSED_PASSWORD')),
                (value, oldValue) => value === oldValue
            )
        ]
    }
}
const addMemberSchema = () => {
    return Yup.object().shape({
        channel_type: Yup.object().nullable()
            .required(text.required(getFields('CHANNEL_TYPE'))),
        channel_value: Yup.string()
            .required(text.required(getFields('MEMBER_EMAIL')))
            .email(text.like_email(getFields('MEMBER_EMAIL')))
    })
}

const addTeamSchema = () => {
    return Yup.object().shape({
        name: Yup.string()
            .required(text.required(getFields('NAME')))
            .min(MIN_LENGTH, text.length(getFields('NAME'), MIN_LENGTH, 'minimum'))
    })
}

const addJobSchema = () => {
    return Yup.object().shape({
        name: Yup.string()
            .required(text.required(getFields('NAME')))
            .min(MIN_LENGTH, text.length(getFields('NAME'), MIN_LENGTH, 'minimum')),
        service_id: Yup.object().nullable()
            .required(text.required(getFields('SERVICE_NAME'))),
        methods: Yup.array().min(1, text.required(getFields('METHODS'), true))
    })
}

const addLogGroupSchema = () => {
    return Yup.object().shape({
        name: Yup.string()
            .required(text.required(getFields('NAME')))
            .min(MIN_LENGTH, text.length(getFields('NAME'), MIN_LENGTH, 'minimum')),
        service_id: Yup.object().nullable()
            .required(text.required(getFields('SERVICE_NAME'))),
        retention: Yup.number().required(text.required(getFields('RETENTION')))
            .min(0, text.must_be_between(getFields('RETENTION'), 1, 100))
    })
}

const statuspageIoSchema = () => {
    return Yup.object().shape({
        name: Yup.string().required(text.required(getFields('NAME'))).min(MIN_LENGTH, text.length(getFields('NAME'), MIN_LENGTH, 'minimum')),
        api_key: Yup.string().required(text.required(getFields('API_KEY'))).min(MIN_LENGTH, text.length(getFields('API_KEY'), MIN_LENGTH, 'minimum')),
        type: Yup.object().nullable().required(text.required(getFields('INTEGRATION_TYPE'))),
        page_id: Yup.string().required(text.required(getFields('INTEGRATION_PAGE_ID'))).min(MIN_LENGTH, text.length(getFields('INTEGRATION_PAGE_ID'), MIN_LENGTH, 'minimum'))
    })
}

const statusIoSchema = () => {
    return Yup.object().shape({
        name: Yup.string().required(text.required(getFields('NAME'))).min(MIN_LENGTH, text.length(getFields('NAME'), MIN_LENGTH, 'minimum')),
        api_key: Yup.string().required(text.required(getFields('API_KEY'))).min(MIN_LENGTH, text.length(getFields('API_KEY'), MIN_LENGTH, 'minimum')),
        api_id: Yup.string().required(text.required(getFields('API_ID'))).min(MIN_LENGTH, text.length(getFields('API_ID'), MIN_LENGTH, 'minimum')),
        type: Yup.object().nullable().required(text.required(getFields('INTEGRATION_TYPE'))),
        page_id: Yup.string().required(text.required(getFields('INTEGRATION_PAGE_ID'))).min(MIN_LENGTH, text.length(getFields('INTEGRATION_PAGE_ID'), MIN_LENGTH, 'minimum'))
    })
}

const resourceIntegrationSchema = () => {
    return Yup.object().shape({
        type: Yup.object().nullable().required(text.required(getFields('INTEGRATION_TYPE'))),
        integration: Yup.object().nullable().required(text.required(getFields('INTEGRATION'))),
        components: Yup.array().min(1, text.required(getFields('COMPONENTS'), true)),
        name: Yup.string().required(text.required(getFields('NAME'))).min(MIN_LENGTH, text.length(getFields('NAME'), MIN_LENGTH, 'minimum')),
        status: Yup.object().nullable().required(text.required(getFields('STATUS')))
    })
}

const statuspageAdvancedSchema = () => {
    return Yup.object().shape({
        description: Yup.string().transform((curr, orig) => (orig === '' ? null : curr)).nullable().min(MIN_LENGTH, text.length(getFields('DESCRIPTION'), MIN_LENGTH, 'minimum')),
        page_name: Yup.string().transform((curr, orig) => (orig === '' ? null : curr)).nullable().min(MIN_LENGTH, text.length(getFields('PAGE_NAME'), MIN_LENGTH, 'minimum')),
        website_url: Yup.string().transform(value => value.trim()).nullable().url(text.invalid_field(getFields('WEBSITE_URL'))),
        privacy_url: Yup.string().transform(value => value.trim()).nullable().url(text.invalid_field(getFields('PRIVACY_URL'))),
        support_url: Yup.string().transform(value => value.trim()).nullable().url(text.invalid_field(getFields('SUPPORT_URL'))),
        ga_tracking: Yup.string().transform((curr, orig) => (orig === '' ? null : curr)).nullable().min(MIN_LENGTH, text.length(getFields('GA_TRACKING'), MIN_LENGTH, 'minimum')),
        logo: Yup.mixed().nullable()
            .test('fileType', text.invalid_file_type(getFields('LOGO')), value => {
                if (!value) {
                    return true
                }
                return value.type && value.type.includes('image/')
            })
            .test('fileSize', text.invalid_file_size(getFields('LOGO'), STATUSPAGE_LOGO_MAX_SIZE.label), value => {
                if (!value) {
                    return true
                }
                const maxSizeInBytes = STATUSPAGE_LOGO_MAX_SIZE.value
                return value.size <= maxSizeInBytes
            }),
        favicon: Yup.mixed().nullable()
            .test('fileType', () => text.invalid_file_type(getFields('FAVICON')), value => {
                if (!value) {
                    return true
                }
                return value.type && (value.type.includes('image/png') || value.type.includes('image/x-icon'))
            })
            .test('fileSize', text.invalid_file_size(getFields('FAVICON'), STATUSPAGE_FAVICON_MAX_SIZE.label), value => {
                if (!value) {
                    return true
                }
                const maxSizeInBytes = STATUSPAGE_FAVICON_MAX_SIZE.value
                return value.size <= maxSizeInBytes
            })
            .test('dimension', text.invalid_dimension(getFields('FAVICON')), async value => {
                if (!value) {
                    return true
                }

                try {
                    const img = new Image()
                    img.src = URL.createObjectURL(value)

                    const loadImage = () => {
                        return new Promise((resolve, reject) => {
                            img.onload = () => resolve(img)
                            img.onerror = () => reject(new Error(text.failed_load_file(getFields('FAVICON'))))
                        })
                    }

                    await loadImage()

                    return img.width === img.height && img.width <= statuspageFaviconMaxDimension
                } catch (error) {
                    return false
                }
            })
    })
}

const statuspageDomainSchema = () => {
    return  Yup.object().shape({
        domain: Yup.string().transform((curr, orig) => (orig === '' ? null : curr)).nullable().min(MIN_LENGTH, text.length(getFields('DOMAIN'), MIN_LENGTH, 'minimum'))
    })
}

const statuspageSettingsSchema = () => {
    return Yup.object().shape({
        name: Yup.string().required(text.required(getFields('NAME'))).min(MIN_LENGTH, text.length(getFields('NAME'), MIN_LENGTH, 'minimum')),
        type: Yup.object().nullable().required(text.required(getFields('STATUSPAGE_TYPE'))),
        ip_addresses: Yup.string().nullable().when(['type', 'ip_addresses', 'password'], (type, ipAddresses, password) => {
            if (type?.value === 'private' && ipAddresses) {
                return Yup.string().test('ip_addresses', text.invalid_field(getFields('IP_ADDRESSES'), true), (value) => {
                    const lines = value?.split('\n')

                    return lines?.every(line => regexPatterns.ipv4WithCIDRPattern.test(line?.trim()))
                })
            }
            if (type?.value === 'private' && !password && !ipAddresses) {
                return Yup.string().required(text.required(getFields('IP_ADDRESSES'), true))
            }
        }),
        password: Yup.string().nullable().when(['type', 'ip_addresses', 'password'], (type, ipAddresses, password) => {
            if (type?.value === 'private' && password) {
                return Yup.string().min(PASSWORD_MIN_LENGTH, text.length(getFields('PASSWORD'), PASSWORD_MIN_LENGTH, 'minimum'))
            }
            if (type?.value === 'private' && !ipAddresses && !password) {
                return Yup.string().required(text.required(getFields('PASSWORD')))
            }
        }),
        subdomain: Yup.string().nullable().required(text.required(getFields('SUBDOMAIN')))
    }, [
        ['ip_addresses', 'password', 'ip_addresses'],
        ['password', 'ip_addresses', 'password'],
        ['ip_addresses', 'ip_addresses', 'password'],
        ['password', 'password', 'ip_addresses']
    ])
}

const statuspageStructureSchema = () => {
    return Yup.object().shape({
        structures: Yup.array().of(
            Yup.object().shape(
                {
                    is_group: Yup.object({
                        value: Yup.boolean(),
                        label: Yup.string()
                    }),
                    group_name: Yup.string().nullable().when('is_group', data => {
                        if (data?.value) {
                            return Yup.string().required(text.required(getFields('GROUP_NAME'))).min(MIN_LENGTH, text.length(getFields('GROUP_NAME'), MIN_LENGTH, 'minimum'))
                        }
                    }),
                    type: Yup.object({
                        value: Yup.string(),
                        label: Yup.string()
                    }),
                    components: Yup.array().nullable().when('type', data => {
                        if (data?.value === 'resource') {
                            return Yup.array().min(1, text.required(getFields('RESOURCE')))
                        }
                    }),
                    custom_components: Yup.array().when('type', data => {
                        if (data?.value === 'custom') {
                            return Yup.array().of(
                                Yup.object().shape({
                                    name: Yup.string().required(text.required(getFields('COMPONENT_NAME'))).min(MIN_LENGTH, text.length(getFields('COMPONENT_NAME'), MIN_LENGTH, 'minimum')),
                                    description: Yup.string().required(text.required(getFields('DESCRIPTION'))).min(MIN_LENGTH, text.length(getFields('DESCRIPTION'), MIN_LENGTH, 'minimum'))
                                })
                            )
                        }
                    })
                }
            )
        )
    })
}

const incidentSchema = () => {
    return Yup.object().shape({
        name: Yup.string().required(text.required(getFields('NAME'))).min(MIN_LENGTH, text.length(getFields('NAME'), MIN_LENGTH, 'minimum')),
        description: Yup.string().required(text.required(getFields('DESCRIPTION'))).min(MIN_LENGTH, text.length(getFields('NAME'), MIN_LENGTH, 'minimum')),
        urgency: Yup.object().nullable().required(text.required(getFields('URGENCY'))),
        components: Yup.array().nullable().when('actions', data => {
            if (data.includes('trigger-statuspage-update')) {
                return Yup.array().min(1, text.required(getFields('COMPONENTS'), true))
            }
        })
    })
}

const serverMonitoringSchema = (formValues) => {
    let schema = Yup.object()
    Object.keys(formValues).forEach(field => {
        switch (field) {
        case 'server_name':
            schema = schema.shape({
                [field]: Yup.string().required(text.required(getFields('NAME'))).min(MIN_LENGTH, text.length(getFields('NAME'), MIN_LENGTH, 'minimum'))
            })
            break
        case 'description':
            schema = schema.shape({
                [field]: Yup.string().required(text.required(getFields('DESCRIPTION'))).min(MIN_LENGTH, text.length(getFields('DESCRIPTION'), MIN_LENGTH, 'minimum'))
            })
            break
        case 'service_id':
            schema = schema.shape({
                [field]: Yup.object().test('is-not-empty', text.required(getFields('SERVICE_NAME')), ({value}) => {
                    return value
                })
            })
            break
        case 'timezone':
            schema = schema.shape({
                [field]: Yup.object().test('is-not-empty', text.required(getFields('timezone')), ({value}) => {
                    return value
                })
            })
            break
        case 'team_id':
            schema = schema.shape({
                [field]: Yup.object().nullable().required(text.required(getFields('TEAM_NAME')), ({value}) => {
                    return value
                })
            })
            break
        default:
            break
        }
    })

    return schema
}

const rulesetSchema = () => {
    return Yup.object().shape({
        name: Yup.string().required(text.required(getFields('NAME'))).min(MIN_LENGTH, text.length(getFields('NAME'), MIN_LENGTH, 'minimum')),
        description: Yup.string().required(text.required(getFields('DESCRIPTION'))).min(MIN_LENGTH, text.length(getFields('NAME'), MIN_LENGTH, 'minimum')),
        rule_type: Yup.object().nullable().required(text.required(getFields('RULE_TYPE'))),
        content_type: Yup.object().nullable().required(text.required(getFields('CONTENT_TYPE'))),
        urgency: Yup.object().nullable().required(text.required(getFields('URGENCY'))),
        level: Yup.object().nullable().required(text.required(getFields('LEVEL'))),
        components: Yup.array().nullable().when('actions', data => {
            if (data.includes('trigger-statuspage-update')) {
                return Yup.array().min(1, text.required(getFields('COMPONENTS'), true))
            }
        }),
        webhook_conditions: Yup.array().when(['rule_type', 'content_type'], (ruleType, contentType) => {
            if (ruleType.value === 'webhook' && contentType) {
                return Yup.array().of(
                    Yup.object().shape({
                        key: Yup.string().when(['value', 'operator'], {
                            is: (operator, value) => contentType.value === 'json-check' && (operator || value),
                            then: Yup.string().required(text.required(getFields('KEY')))
                        }),
                        operator: Yup.object().nullable().when(['key', 'value'], {
                            is: (key, value) => (contentType.value === 'json-check' && key) || value,
                            then: Yup.object().nullable().required(text.required(getFields('OPERATOR')))
                        }),
                        value: Yup.string().when(['key', 'operator'], {
                            is: (key, operator) => (contentType.value === 'json-check' && key) || operator,
                            then: Yup.string().required(text.required(getFields('VALUE')))
                        })
                    }, [
                        ['key', 'value'], ['key', 'operator'],
                        ['operator', 'value']
                    ])
                ).test('at-least-one', 'At least one condition is required', data => {
                    const filteredData = data?.filter(condition => Object.values(condition).some(value => value !== undefined && value !== null))
                    return !!filteredData?.length
                }).test('max-size', 'Conditions limit exceeded. Max size is 5', data => {
                    const filteredData = data?.filter(condition => Object.values(condition).some(value => value !== undefined && value !== null))
                    return filteredData?.length <= 5
                })
            }
        })
    })
}

const phoneCallTemplateSchema = () => {
    return Yup.object().shape({
        name: Yup.string().required(text.required(getFields('NAME'))).min(MIN_LENGTH, text.length(getFields('NAME'), MIN_LENGTH, 'minimum')),
        description: Yup.string().required(text.required(getFields('DESCRIPTION'))).min(MIN_LENGTH, text.length(getFields('NAME'), MIN_LENGTH, 'minimum')),
        teams:  Yup.object().nullable().shape({
            value: Yup.string().required(text.required(getFields('TEAMS')))
        }),
        voice: Yup.object().nullable().required(text.required(getFields('VOICE'))),
        language: Yup.object().nullable().required(text.required(getFields('LANGUAGE'))),
        text: Yup.object().shape({
            plain: Yup.string().required(text.required(getFields('TEXT')))
                .min(MIN_LENGTH, text.length(getFields('TEXT'), MIN_LENGTH, 'minimum'))
                .max(300, text.length(getFields('TEXT'), 300, 'maximum'))
        })
    })
}
export {
    addResourceSchema,
    customerSchema,
    usernameSchema,
    codeSchema,
    teamSchema,
    feedbackSchema,
    notificationEmailSchema,
    voucherSchema,
    channelTypeSchema,
    webhookSchema,
    webhookURLSchema,
    automationSchema,
    reminderSchema,
    deletionReasonSchema,
    getPasswordErrors,
    addMemberSchema,
    addTeamSchema,
    addJobSchema,
    statuspageIoSchema,
    statusIoSchema,
    resourceIntegrationSchema,
    couponSchema,
    statuspageSettingsSchema,
    statuspageStructureSchema,
    statuspageAdvancedSchema,
    statuspageDomainSchema,
    incidentSchema,
    rulesetSchema,
    addDynamicRulesetSchema,
    scheduleSchema,
    serverMonitoringSchema,
    logManagementFiltersSchema,
    addLogGroupSchema,
    phoneCallTemplateSchema,
    telegramSchema,
    mfaCodeSchema
}
